import React, { useEffect, useRef, useState } from 'react';

import { useFormikContext } from 'formik';
import { isUndefined } from 'lodash';

import { Button } from '@components/Button';
import Select from '@components/Formik/Select';
import LeaveFormConfirmationModal from '@components/Modals/LeaveFormConfirmationModal';

import InfiniteScrollSelect from '../InfiniteScrollSelect';
import RadioField from '../RadioField';
import TextArea from '../TextArea';
import { options } from './utils';

import { useModal, useStopBrowserNavigate } from '@use-cases/districts';

import { useFetchDistrictClubs as useFetchClubs } from '@repositories/districts';
import { useFetchClubSelectOptionsOnQuery as useFetchClubsOnSearch } from '@repositories/memberSearch';

import { getClubDisplayName } from '@utils/club-name';
import { localizedNavigate } from '@utils/localized-navigate';

import { useTranslation } from '@external/react-i18next';

interface Props {
  id: string;
  isDistrict: boolean;
  riDistrictId?: string;
  onSubmitClick: (clubId?: string | null) => void;
  progressListOptions: {
    label: string;
    value: string;
  }[];
}

interface progressValues {
  progress: string;
  candidateCheck?: string;
  additionalNotes?: string;
  confirmAsMember?: boolean;
  clubName?: string;
}

export type SelectOption = {
  label: string | JSX.Element;
  value: string;
  type?: string | null;
  isDisabled?: boolean;
};

const CandidateProgress: React.FC<Props> = ({
  id,
  isDistrict,
  riDistrictId,
  onSubmitClick,
  progressListOptions,
}) => {
  const { t } = useTranslation();

  const candidateMemberOption =
    'candiate-details-update-progress.select.option6';

  const { values, setFieldValue } = useFormikContext<progressValues>();

  const { isShowing, show } = useModal(window.stopBrowserNavigate);

  const [initialData, setInitialData] = useState<SelectOption[]>([]);

  const [searchData, setSearchData] = useState<SelectOption[]>([]);

  const [scrollLimit, setScrollLimit] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [input, setInput] = useState<string>('');
  const { progress, candidateCheck, confirmAsMember, clubName } = values;
  const additionalNotesRef = useRef<HTMLInputElement>(null);

  const [fetchClubs, { data, loading }] = useFetchClubs();

  const [
    fetchClubsOnSearch,
    { data: dataSearch, loading: loadingSearch },
  ] = useFetchClubsOnSearch();

  useEffect(() => {
    if (input === '') {
      fetchClubs({
        variables: {
          districtId: id,
          pageSize: 100,
          page,
        },
      });
      setPage(prev => prev + 1);
    } else if (input !== '') {
      fetchClubsOnSearch({
        variables: {
          clubName: input,
          riDistrictId: Number(riDistrictId),
        },
      });
    }
  }, [input, scrollLimit]);

  useEffect(() => {
    const sortedData = data?.districtClubs.clubs
      .filter(club => club.status !== 'Terminated')
      .map(club => ({
        label: getClubDisplayName(club.name, club.type, t),
        value: club.id,
      }));
    if (sortedData && sortedData.length > 0)
      setInitialData([...initialData, ...sortedData]);
  }, [data]);

  useEffect(() => {
    const searchSortedData = dataSearch?.clubs.results
      ?.filter(club => club.status !== 'Terminated')
      .map(club => ({
        label: getClubDisplayName(club.fullName, club.type, t),
        value: club.id,
      }));
    setSearchData(searchSortedData || []);
  }, [dataSearch]);

  useEffect(() => {
    if (isUndefined(confirmAsMember) && !confirmAsMember) {
      additionalNotesRef?.current?.focus();
    }
  }, [values]);

  const modalBackHandler = () => {
    show(true);
  };

  const { globalHide } = useStopBrowserNavigate({
    showModal: show,
    isNextStepVisited: true,
    onNavigate: modalBackHandler,
  });

  const handleProgressSelectChange = (progressValue: string) => {
    if (progressValue === candidateMemberOption) {
      setFieldValue('progress', progressValue);
      setFieldValue('confirmAsMember', true);
      setFieldValue('additionalNotes', undefined);
      setFieldValue('clubName', '');
    } else {
      setFieldValue('progress', progressValue);
      setFieldValue('confirmAsMember', false);
      setFieldValue('candidateCheck', undefined);
    }
  };

  const cancelButtonHandler = () => {
    setFieldValue('progress', undefined);
    setFieldValue('confirmAsMember', undefined);
    setFieldValue('candidateCheck', undefined);
    setFieldValue('additionalNotes', undefined);
    setFieldValue('clubName', undefined);
    if (!isDistrict) return localizedNavigate(`/club/${id}/manage-membership`);
    return localizedNavigate(`/district/${id}/manage-membership`);
  };

  function validateContinue(): boolean {
    if (isDistrict) {
      if (progress === candidateMemberOption) return clubName === '';
      return false;
    }
    if (progress === candidateMemberOption) return isUndefined(candidateCheck);
    return false;
  }

  function clubsListData(): SelectOption[] {
    if (input !== '') return searchData;
    return initialData;
  }

  return (
    <>
      <Select
        name="progress"
        required
        label={t(
          'candidate-details.form.update-progress-label',
          'Update progress'
        )}
        options={progressListOptions}
        onChange={handleProgressSelectChange}
        descriptionHighlighted
      />

      {progress && !confirmAsMember && !isDistrict && (
        <TextArea
          areaclasses="h-40"
          name="additionalNotes"
          label={t('candidate-details.form.update-notes-label', 'Notes')}
          characters={250}
          displayCharLimit
        />
      )}

      {progress && confirmAsMember && !isDistrict && (
        <RadioField
          name="candidateCheck"
          label="Has this candidate already been confirmed as a member?"
          required
          options={options}
        />
      )}

      {progress && !confirmAsMember && isDistrict && (
        <TextArea
          areaclasses="h-40"
          name="additionalNotes"
          label={t('candidate-details.form.update-notes-label', 'Notes')}
          characters={250}
          displayCharLimit
        />
      )}

      {progress && confirmAsMember && isDistrict && (
        <InfiniteScrollSelect
          name="clubName"
          required
          searchable
          label={t('candidate-details.form.club-select-label', 'Club')}
          options={clubsListData()}
          descriptionHighlighted
          onScrollEnd={() => setScrollLimit(prev => prev + 1)}
          loading={loading || loadingSearch}
          setInputValue={setInput}
          filterOption={() => true}
        />
      )}

      {progress && (
        <>
          <Button
            type="submit"
            full
            disabled={validateContinue()}
            clickHandler={onSubmitClick}
          >
            {t('candidate-details.form.continue-label', 'Continue')}
          </Button>
          <Button
            full
            text
            type="button"
            className="mt-6"
            clickHandler={modalBackHandler}
          >
            {t('candidate-details.form.cancel-label', 'Cancel')}
          </Button>
        </>
      )}
      <LeaveFormConfirmationModal
        isOpen={isShowing}
        closeModal={globalHide}
        onConfirm={cancelButtonHandler}
      />
    </>
  );
};

export default CandidateProgress;
